import {
  ClassGradesFeatureConfigSchema,
  type ClassGradesFeatureConfig,
  type ClassGradesFeatureConfigWrite
} from '@school-journey/shared-types'

import type { ScreenFeatureEntry } from './entry'

/**
 * Global `classGrades` (SCH-1783).
 *
 * absentIsEmpty: the block must never be in the fleet-default row (see the
 * schema's docblock), and 24 of 33 school rows have none (read-only production
 * query, `SystemConfiguration`, 2026-10-02: classGrades on 9 of 33).
 *
 * Fallback: grades off. The server denies unless `enabled === true` and turns
 * each audience on only for `=== true`
 * (`apps/backend/src/services/config/class-grades-audience.ts:161-166`).
 * `visibleForRoles` is required by the schema and inert for this key; `[]` is
 * the server's backfill for a feature with no canonical default
 * (`apps/backend/src/services/config/global-feature-defaults.ts:88-90`).
 *
 * toWire: every save writes the FULL block (SCH-1783 KTD20), unchanged and
 * fallback-only fields included, so the stored row means what it says without
 * relying on the read's backfill. The one entry that writes its fallback back,
 * on purpose: the fallback is the server's own reading of an absent field.
 *
 * The grade list is the exception (plan 2026-10-13-1846 KTD6): `toWire` sends
 * `studentGradeLevels` only when the save changed it, as `null` to clear it
 * back to "All grades". The server keeps the stored list when the key is
 * missing, so a switch-only save cannot drop it, and settles `null` without
 * storing it (`settleGradeLevelsWrite`,
 * `apps/backend/src/services/config/global-features.ts`). Wire and change are
 * the WRITE shape for that `null`; after such a save the cached block still
 * holds it, so `toValue` reads it as no list.
 */
export const classGrades: ScreenFeatureEntry<
  'global',
  'classGrades',
  ClassGradesFeatureConfigWrite,
  ClassGradesFeatureConfig,
  Partial<ClassGradesFeatureConfigWrite>
> = {
  resource: 'global',
  wireName: 'classGrades',
  schema: ClassGradesFeatureConfigSchema,
  writeMode: 'sparse',
  absentIsEmpty: true,
  fallback: {
    enabled: false,
    visibleForRoles: [],
    students: false,
    parents: false,
    admins: false
  },
  toWire: (change, current) => {
    const next = { ...current, ...change }
    return {
      enabled: next.enabled,
      visibleForRoles: next.visibleForRoles,
      students: next.students,
      parents: next.parents,
      admins: next.admins,
      ...('studentGradeLevels' in change ? { studentGradeLevels: change.studentGradeLevels } : {})
    }
  },
  toValue: ({ studentGradeLevels, ...rest }) =>
    studentGradeLevels === null || studentGradeLevels === undefined ? rest : { ...rest, studentGradeLevels }
}
